import React, { useEffect, useState, Fragment } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { createRoot } from '@wordpress/element';
import { displayErrorMessage, displayLoader, ConnectionContext, getConnectionInfo, getEndpoint } from './utils';
import { getStoredData } from "./utils/data-pulls";
import { Sidebar } from "./components";
import { UpcomingGamesWidget } from "./components/widgets/upcomingGamesWidget";
import { AcaSelect } from "./components/forms/acaSelect";
import { Option } from '@mui/base/Option';
import { MatchBox } from "./components";


const LiveScoresApp = (props) => {
    const { 
        ...restProps 
    } = props;

    // Error and load handling
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [ matchesLoaded, setMatchesLoaded ] = useState(false);

    // Connection Information
    const [connectionInfo, setConnectionInfo] = useState(null);
    const [ activeUIDs, setActiveUIDs ] = useState(null);
    
    const [lastUpdatedTime, setLastUpdatedTime] = useState(null);
    const [ refreshCounter, setRefreshCounter ] = useState(0);
    const [ currentTime, setCurrentTime ] = useState(new Date());

    const [ matches, setMatches ] = useState(null);


    /**
     * Get our connection info and list of competitions
     */
    useEffect(() => {

        // Get our connection info
        getConnectionInfo().then(
            (result) => {
                setConnectionInfo(result.data);
            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

        // Get competition list
        let args = [
            {
                key: 'activeUIDs'
            }
        ];
        getStoredData(args).then(
            (result) => {

                // Set Active UIDs
                if ( result.data.activeUIDs ) {
                    setActiveUIDs(result.data.activeUIDs);
                }

                // Set loaded
                setIsLoaded(true);

            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

    }, []);


    /**
     * Get live game list, and refresh based on refreshCounter
     */
    useEffect(() => {
        if ( !connectionInfo || !activeUIDs ) return;
        setMatchesLoaded(false);

        // Get live match data
        const liveEndpoint = getEndpoint('match_list_live', {
            tenant: connectionInfo.tenant
        }, {
            organizationIdFilter: connectionInfo.organization_id
        }, connectionInfo.environment);
        
        fetch(liveEndpoint, {
            method: 'GET',
            headers: {
                'API_KEY': connectionInfo.api_key
            },
        })
          .then(res => res.json())
          .then(
            (result) => {

                /**
                 * API will return results for each province. 
                 * Use the list of competitions entered on the website to filter only to live matches we want to view
                 */
                let validMatches = [];
                result.forEach((match, i) => {
                    const exists = activeUIDs.includes(match.competition.id);
                    if ( exists ) {
                        validMatches.push(match);
                    }
                });

                /**
                 * Sort the matches into associative arrays by competition
                 */
                let sortedMatches = {};
                validMatches.forEach((match, i) => {
                    const competition = match.competition;

                    // Create the competition in the array if it doesn't already exist
                    if ( !sortedMatches.hasOwnProperty(competition.id) ) {
                        sortedMatches[competition.id] = {
                            name: competition.name,
                            matches: []
                        };
                    }

                    // Add the match to its competition
                    sortedMatches[competition.id].matches.push(match);

                });
                
                if ( validMatches.length == 0 ) {
                    setMatches(null);
                } else {
                    setMatches(sortedMatches);
                }
                setMatchesLoaded(true);
                setIsLoaded(true);
            },
            (error) => {
                setMatchesLoaded(true);
                setIsLoaded(true);
                setError(error);
                console.log(error);
            }
          );

    }, [connectionInfo, activeUIDs, refreshCounter]);


    /**
     * Setup countdown to refresh live matches
     * Also get and update the current time
     */
    useEffect(() => {
        if ( error ) return;
        const refreshRate = 60; // Every 60 seconds

        setTimeout(() => {
            setRefreshCounter(a => a + 1);
            setCurrentTime(new Date());
        }, refreshRate * 1000);
      
      }, [refreshCounter]);


    /**
     * Update 'last updated' ticker
     */
    useEffect(()=>{
        if ( error ) return;
        let secondsSinceUpdate = 0;
        const refreshRate = 10; // Time in seconds

        setLastUpdatedTime('now');

        const interval = setInterval(() => {
            secondsSinceUpdate += 1;
            const secondsStr = secondsSinceUpdate == 1 ? ' second ago' : ' seconds ago';
            setLastUpdatedTime(secondsSinceUpdate + secondsStr);
        }, refreshRate * 100);

        return () => clearInterval(interval);
    }, [matches, refreshCounter]);


    // Bail if we don't have data yet
    if (!isLoaded ) {
        return displayLoader('acaLoader--main');
    }

    return (
        <ConnectionContext.Provider value={connectionInfo}>
            <section className="acaPage acaPage--withSidebar acaPage--liveScores">
                
                <div className="acaMain">
                    <ErrorBoundary fallback={displayErrorMessage()}>
                        <div className="acaBar acaBar--simple">
                            <div className="acaBar__inner">
                                <div className="acaBar__col acaBar__col--main">
                                    { currentTime ? (
                                        <h2 className="acaH1">{currentTime.toLocaleString('en-US', {month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short', timeZone: acaTimezone })}</h2>
                                    ) : '' }
                                    
                                    <p>{`Last updated ${lastUpdatedTime}`}</p>
                                </div>
                            </div>
                        </div>
                    </ErrorBoundary>
                    <ErrorBoundary fallback={displayErrorMessage()}>
                        { matchesLoaded ? matches ? (
                            ( Object.keys(matches).map((key, i) => (
                                <div key={i} className="acaScheduleGrid__section">
                                    <h3 className="acaH2 acaScheduleGrid__subtitle">{ matches[key].name }</h3>
                                    { matches[key].matches.map((match, n) => (
                                        <MatchBox key={n} includeLink={true} data={match} />
                                    )) }
                                </div>
                            ) ) )
                        ) : displayErrorMessage('No live matches currently running. Check back later.') : (
                            displayLoader('acaLoader--main')
                        ) }
                    </ErrorBoundary>
                </div>

                <Sidebar soccerImg>
                    <ErrorBoundary fallback={displayErrorMessage()}>
                        <UpcomingGamesWidget />
                    </ErrorBoundary>
                </Sidebar>

            </section>
        </ConnectionContext.Provider>
    );
}

export default LiveScoresApp;

// Render Match Page
const appContainer = document.getElementById('liveScoresApp');
const root = createRoot(appContainer);
root.render(<LiveScoresApp />);